import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePaymentContext } from 'features/payment';
import { IPaymentMethod, IReloadPrepaidCard } from 'features/payment/types';
import { isApplePay, isGooglePay } from 'features/payment/utils';

import { IServerOrder } from '@rbi-ctg/menu';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';

import { useRequestApplePayPayment } from './use-request-apple-pay-payment';
import { useRequestGooglePayPayment } from './use-request-google-pay-payment';

const getPrepaidBalance = (prepaidPaymentMethod: IPaymentMethod | undefined) => {
  if (prepaidPaymentMethod && prepaidPaymentMethod.prepaid) {
    if (prepaidPaymentMethod.prepaid.feFormattedCurrentBalance) {
      return prepaidPaymentMethod.prepaid.feFormattedCurrentBalance;
    } else if (prepaidPaymentMethod.prepaid.currentBalance) {
      return prepaidPaymentMethod.prepaid.currentBalance;
    }
  }
  return 0;
};

export const useFirstDataPrepaidReload = ({
  serverOrder,
  reloadAmount,
  billingCountry,
}: {
  serverOrder: IServerOrder | null;
  reloadAmount: number;
  billingCountry: ISOs;
}) => {
  const {
    reloadPrepaidCard,
    checkoutPaymentMethod,
    prepaidReloadPaymentMethodId,
    getPaymentMethods,
  } = usePaymentContext();
  const [isReloadLoading, setIsReloadLoading] = useState(false);

  const reloadWithApplePay = isApplePay(prepaidReloadPaymentMethodId);
  const reloadWithGooglePay = isGooglePay(prepaidReloadPaymentMethodId);

  const prepaidBalance = getPrepaidBalance(checkoutPaymentMethod);
  const { countryCode } = getCountryAndCurrencyCodes(billingCountry);
  const prepaidFdAccountId =
    checkoutPaymentMethod?.accountIdentifier ?? checkoutPaymentMethod?.fdAccountId ?? '';

  const args: IReloadPrepaidCard = useMemo(
    () => ({
      countryCode,
      fundValue: reloadAmount,
      prepaidBalance,
      prepaidFdAccountId,
      fdAccountId: prepaidReloadPaymentMethodId,
    }),
    [countryCode, prepaidBalance, prepaidFdAccountId, prepaidReloadPaymentMethodId, reloadAmount]
  );

  const {
    requestPayment: requestGooglePayPayment,
    isLoading: isGooglePayLoading,
    data: googlePayPaymentDetails,
  } = useRequestGooglePayPayment({
    googlePay: {
      billingCountry,
      total: 0,
      reloadAmount,
      isForPrepaidReload: true,
    },
  });
  const {
    requestPayment: requestApplePayPayment,
    isLoading: isApplePayLoading,
  } = useRequestApplePayPayment({
    applePay: {
      serverOrder,
      tip: 0,
      reloadAmount,
      billingCountry,
      isForPrepaidReload: true,
    },
  });

  useEffect(() => {
    async function handleReload() {
      if (reloadAmount === 0) {
        return;
      }
      setIsReloadLoading(true);

      if (googlePayPaymentDetails?.googlePayDetails) {
        await reloadPrepaidCard({
          ...args,
          googlePayDetails: googlePayPaymentDetails?.googlePayDetails,
        });
      }

      setIsReloadLoading(false);
    }
    handleReload();
  }, [args, googlePayPaymentDetails?.googlePayDetails, reloadAmount, reloadPrepaidCard]);

  const handleApplePayReload = useCallback(async () => {
    setIsReloadLoading(true);
    try {
      const paymentDetails = await requestApplePayPayment();
      if (paymentDetails?.applePayDetails) {
        const updatedBalance = await reloadPrepaidCard({
          ...args,
          applePayDetails: paymentDetails.applePayDetails,
        });

        // If reload failed (returned undefined), throw an error
        if (!updatedBalance) {
          throw new Error('Failed to reload gift card');
        }

        // Wait for payment methods to be refetched after reload to ensure state is updated
        // The mutation has awaitRefetchQueries: true, but we also call getPaymentMethods
        // to ensure the payment methods state is updated before committing
        await getPaymentMethods();
      }
    } finally {
      setIsReloadLoading(false);
    }
  }, [args, reloadPrepaidCard, requestApplePayPayment, getPaymentMethods]);

  const handleGooglePayReload = useCallback(() => {
    // Google Pay reload is handled by useEffect when paymentDetails are available
    requestGooglePayPayment();
  }, [requestGooglePayPayment]);

  const handleStandardReload = useCallback(async () => {
    setIsReloadLoading(true);
    try {
      const updatedBalance = await reloadPrepaidCard(args);

      // If reload failed (returned undefined), throw an error
      if (updatedBalance === undefined) {
        throw new Error('Failed to reload gift card');
      }

      // Wait for payment methods to be refetched after reload to ensure state is updated
      await getPaymentMethods();
    } finally {
      setIsReloadLoading(false);
    }
  }, [args, reloadPrepaidCard, getPaymentMethods]);

  const reloadPrepaid = useCallback(async () => {
    if (reloadAmount === 0) {
      return;
    }

    try {
      if (reloadWithApplePay) {
        await handleApplePayReload();
        return;
      }

      if (reloadWithGooglePay) {
        handleGooglePayReload();
        return;
      }

      await handleStandardReload();
    } catch (error) {
      // Re-throw the error so the caller can handle loading state cleanup
      // This ensures that loading states are properly cleared
      logger.error({ error, message: 'Error in reloadPrepaid' });
      throw error;
    }
  }, [
    reloadAmount,
    reloadWithApplePay,
    reloadWithGooglePay,
    handleApplePayReload,
    handleGooglePayReload,
    handleStandardReload,
  ]);

  return { isLoading: isReloadLoading || isApplePayLoading || isGooglePayLoading, reloadPrepaid };
};
